import { useState } from 'react';
import type { AdminUser } from '../types';
import { Icon, Avatar, fmt } from '../atoms';
import { CAPS, FLAGS } from '../data';

function Shell({ eyebrow, icon, title, desc, children, actions, onClose, width }: {
  eyebrow: string; icon: string; title: string; desc?: string;
  children: React.ReactNode; actions: React.ReactNode;
  onClose: () => void; width?: number;
}) {
  return (
    <div className="modal">
      <div className="scrim" onClick={onClose} />
      <div className="box" style={width ? { width } : undefined}>
        <div className="m-top">
          <div className="m-eyebrow"><Icon name={icon} size={13} />{eyebrow}</div>
          <h2>{title}</h2>
          {desc && <p>{desc}</p>}
        </div>
        <div className="m-body">{children}</div>
        <div className="m-acts">{actions}</div>
      </div>
    </div>
  );
}

function ResetFlow({ u, onClose, onDone }: {
  u: AdminUser; onClose: () => void;
  onDone: (msg: string, patch?: Partial<AdminUser>) => void;
}) {
  const [method, setMethod] = useState<'link' | 'force'>('link');
  const [sent, setSent] = useState(false);

  if (sent) {
    return (
      <Shell eyebrow="Password" icon="mail" title="Reset on its way"
        desc={method === 'link'
          ? `A secure reset link has been emailed to ${u.email}. It expires in 60 minutes.`
          : `${u.name} will be required to set a new password at their next sign-in. Current sessions have been revoked.`}
        onClose={onClose}
        actions={<button className="btn pri" onClick={() => onDone(`Password reset sent to ${u.name}`)} style={{ minWidth: 110, justifyContent: 'center' }}>Done</button>}>
        <div className="card" style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar size={38} hue={u.hue} initials={u.initials} />
          <div><div style={{ fontWeight: 600 }}>{u.name}</div><div className="mono" style={{ fontSize: 12, color: 'var(--faint)' }}>{u.email}</div></div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell eyebrow="Password" icon="key" title="Reset password"
      desc={`Choose how ${u.name} recovers access. Admins never see or set the password directly.`}
      onClose={onClose}
      actions={<>
        <div className="grow" />
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn pri" onClick={() => setSent(true)}>
          {method === 'link'
            ? <><Icon name="mail" size={15} />Send reset link</>
            : <><Icon name="power" size={15} />Force reset</>}
        </button>
      </>}>
      <button className={'choice' + (method === 'link' ? ' on' : '')} onClick={() => setMethod('link')}>
        <span className="radio" />
        <div className="tx">
          <div className="t">Email a reset link</div>
          <div className="d">Sends a one-time link to {u.email}, valid for 60 minutes. Least disruptive.</div>
        </div>
      </button>
      <button className={'choice' + (method === 'force' ? ' on' : '')} onClick={() => setMethod('force')}>
        <span className="radio" />
        <div className="tx">
          <div className="t">Force reset at next sign-in</div>
          <div className="d">Revokes all active sessions and requires a new password immediately. Use for suspected compromise.</div>
        </div>
      </button>
    </Shell>
  );
}

function CapsFlow({ u, onClose, onDone }: {
  u: AdminUser; onClose: () => void;
  onDone: (msg: string, patch?: Partial<AdminUser>) => void;
}) {
  const [tier, setTier] = useState(u.tier);
  const [tok, setTok] = useState(Math.round(u.capTokens / 1000));
  const [cost, setCost] = useState(u.capCost);

  return (
    <Shell eyebrow="AI admin" icon="sliders" title="Adjust AI caps"
      desc={`Per-account ceiling. When either limit is hit, Jotly pauses AI features for ${u.name} until reset or raised.`}
      onClose={onClose}
      actions={<>
        <div className="grow" />
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn pri" onClick={() => onDone(
          `Caps updated for ${u.name} — ${fmt.tokens(tok * 1000)} tokens · ${fmt.cost(cost)}`,
          { capTokens: tok * 1000, capCost: cost, tier }
        )}><Icon name="check" size={15} />Save caps</button>
      </>}>
      <div className="field">
        <label>Tier</label>
        <div className="segsel">
          <button className={tier === 'free' ? 'on' : ''} onClick={() => { setTier('free'); setTok(100); setCost(3); }}>Free · 100k · £3</button>
          <button className={tier === 'approved' ? 'on' : ''} onClick={() => { setTier('approved'); setTok(2000); setCost(20); }}>Approved · 2M · £20</button>
        </div>
      </div>
      <div className="capfields">
        <div className="field">
          <label>Token cap</label>
          <div className="inp-affix">
            <input type="number" value={tok} min={10} step={10} onChange={e => setTok(+e.target.value || 0)} />
            <span className="suf">k / mo</span>
          </div>
        </div>
        <div className="field">
          <label>Cost cap</label>
          <div className="inp-affix">
            <span className="pre">£</span>
            <input type="number" value={cost} min={0} step={0.5} onChange={e => setCost(+e.target.value || 0)} />
            <span className="suf">/ mo</span>
          </div>
        </div>
      </div>
      <div className="field" style={{ marginTop: 14 }}>
        <div className="rangewrap">
          <input type="range" min={10} max={3000} step={10} value={tok} onChange={e => setTok(+e.target.value)} />
          <span className="rv mono">{fmt.tokens(tok * 1000)}</span>
        </div>
      </div>
      <p className="hint">Hitting a cap never deletes data — capture and recall keep working; only new AI generation pauses.</p>
    </Shell>
  );
}

function DisableFlow({ u, onClose, onDone }: {
  u: AdminUser; onClose: () => void;
  onDone: (msg: string, patch?: Partial<AdminUser>) => void;
}) {
  const [reason, setReason] = useState('');
  const reasons = ['Suspected abuse / scripting', 'Payment / cost overrun', 'User request', 'Duplicate account', 'Security review'];

  return (
    <Shell eyebrow="Account" icon="ban" title="Disable account"
      desc={`${u.name} will be signed out and blocked from signing in. Their data is retained and everything can be restored by re-enabling.`}
      onClose={onClose}
      actions={<>
        <div className="grow" />
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn bad" onClick={() => onDone(`${u.name}'s account disabled`, { status: 'disabled' })}>
          <Icon name="ban" size={15} />Disable account
        </button>
      </>}>
      <div className="field">
        <label>Reason (logged to audit trail)</label>
        <div className="stack" style={{ gap: 8 }}>
          {reasons.map(r => (
            <button key={r} className={'choice' + (reason === r ? ' on' : '')} style={{ padding: '11px 13px' }} onClick={() => setReason(r)}>
              <span className="radio" />
              <div className="tx"><div className="t" style={{ fontWeight: 500, fontSize: 13.5 }}>{r}</div></div>
            </button>
          ))}
        </div>
      </div>
    </Shell>
  );
}

function EnableFlow({ u, onClose, onDone }: {
  u: AdminUser; onClose: () => void;
  onDone: (msg: string, patch?: Partial<AdminUser>) => void;
}) {
  return (
    <Shell eyebrow="Account" icon="power" title="Re-enable account"
      desc={`Restores full access for ${u.name}. Prior caps and data are intact; AI usage resumes from where it paused.`}
      onClose={onClose}
      actions={<>
        <div className="grow" />
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn pri" onClick={() => onDone(`${u.name}'s account re-enabled`, { status: 'active' })}>
          <Icon name="check" size={15} />Re-enable
        </button>
      </>}>
      <div className="card" style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <Avatar size={38} hue={u.hue} initials={u.initials} />
        <div><div style={{ fontWeight: 600 }}>{u.name}</div><div className="mono" style={{ fontSize: 12, color: 'var(--faint)' }}>{u.email}</div></div>
      </div>
    </Shell>
  );
}

function ApproveFlow({ u, onClose, onDone }: {
  u: AdminUser; onClose: () => void;
  onDone: (msg: string, patch?: Partial<AdminUser>) => void;
}) {
  const [tier, setTier] = useState<'free' | 'approved'>('approved');

  return (
    <Shell eyebrow="Approvals" icon="check" title="Approve registration"
      desc={`Grant ${u.name} a full account. They keep everything captured on the free tier; caps lift to the tier you pick.`}
      onClose={onClose}
      actions={<>
        <div className="grow" />
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn pri" onClick={() => {
          const caps = CAPS[tier];
          onDone(`${u.name} approved on the ${tier === 'approved' ? 'Approved' : 'Free'} tier`,
            { status: 'active', tier, capTokens: caps.tokens, capCost: caps.cost });
        }}><Icon name="check" size={15} />Approve user</button>
      </>}>
      <div className="card" style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
        <Avatar size={38} hue={u.hue} initials={u.initials} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600 }}>{u.name}</div>
          <div className="mono" style={{ fontSize: 12, color: 'var(--faint)' }}>{u.email}</div>
        </div>
        {u.flags.includes('dupe') && <span className="badge bad"><span className="d" />Dupe signal</span>}
      </div>
      {u.flags.includes('dupe') && (
        <p className="hint" style={{ color: 'var(--bad)' }}>
          Heads up: this registration matches duplicate-account signals. Review before approving.
        </p>
      )}
      <div className="field">
        <label>Grant tier</label>
        <div className="segsel">
          <button className={tier === 'free' ? 'on' : ''} onClick={() => setTier('free')}>Keep Free · 100k · £3</button>
          <button className={tier === 'approved' ? 'on' : ''} onClick={() => setTier('approved')}>Approved · 2M · £20</button>
        </div>
      </div>
    </Shell>
  );
}

function InvestigateFlow({ u, onClose, onDone }: {
  u: AdminUser; onClose: () => void;
  onDone: (msg: string, patch?: Partial<AdminUser>) => void;
}) {
  return (
    <Shell eyebrow="Trust & safety" icon="eye" title={'Investigate ' + u.name} width={520}
      desc="Review the signals, then throttle, disable, or clear. Every action is written to the audit trail."
      onClose={onClose}
      actions={<>
        <button className="btn" onClick={() => onDone(`Flags cleared for ${u.name}`, { flags: [] })}>Clear flags</button>
        <div className="grow" />
        <button className="btn" onClick={() => onDone(`AI throttled for ${u.name}`,
          { capTokens: Math.round(u.capTokens * 0.25), capCost: +(u.capCost * 0.25).toFixed(2) })}>
          <Icon name="gauge" size={15} />Throttle 75%
        </button>
        <button className="btn bad" onClick={() => onDone(`${u.name}'s account disabled`, { status: 'disabled' })}>
          <Icon name="ban" size={15} />Disable
        </button>
      </>}>
      <div className="card" style={{ padding: '4px 16px' }}>
        {u.flags.map(f => {
          const m = FLAGS[f];
          return (
            <div className="dr-flag" key={f}>
              <span className={'ic ' + m.sev}><Icon name={m.sev === 'bad' ? 'alert' : 'gauge'} size={16} /></span>
              <div className="tx"><div className="t">{m.label}</div><div className="d">{m.desc}</div></div>
            </div>
          );
        })}
      </div>
      <div className="statgrid" style={{ marginTop: 14 }}>
        <div className="stat"><div className="l">Requests</div><div className="v">{fmt.int(u.reqs)}</div></div>
        <div className="stat"><div className="l">Cost vs baseline</div><div className="v">{u.baseline ? '×' + (u.cost / u.baseline).toFixed(1) : '—'}</div></div>
      </div>
    </Shell>
  );
}

export function FlowModal({ type, user, onClose, onDone }: {
  type: string;
  user: AdminUser;
  onClose: () => void;
  onDone: (msg: string, patch?: Partial<AdminUser>) => void;
}) {
  if (type === 'reset') return <ResetFlow u={user} onClose={onClose} onDone={onDone} />;
  if (type === 'caps') return <CapsFlow u={user} onClose={onClose} onDone={onDone} />;
  if (type === 'disable') return <DisableFlow u={user} onClose={onClose} onDone={onDone} />;
  if (type === 'enable') return <EnableFlow u={user} onClose={onClose} onDone={onDone} />;
  if (type === 'approve') return <ApproveFlow u={user} onClose={onClose} onDone={onDone} />;
  if (type === 'investigate') return <InvestigateFlow u={user} onClose={onClose} onDone={onDone} />;
  return null;
}
